import React, { useState, useEffect } from 'react';
import { FiBell } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import Dropdown from '../../components/ui/dropdown';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationBell = () => {
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const history = useHistory();
	const {
		notificationCount,
		recentNotifications,
		loading,
		recentLoading,
		sseConnected,
		fetchRecentNotifications,
		markAllAsSeen,
		markNotificationAsSeen
	} = useNotifications();

	const DROPDOWN_LIMIT = 5; // Show only 5 most recent in dropdown


	const handleDropdownOpen = async () => {
		setDropdownOpen(true);
		// Just fetch notifications - don't mark as seen
		fetchRecentNotifications(DROPDOWN_LIMIT);
	};

	const handleDropdownClose = () => {
		setDropdownOpen(false);
	};

	const handleNotificationClick = async (notification) => {
		// Mark this specific notification as seen
		await markNotificationAsSeen(notification.id);

		// Check if this notification has a valid submission to navigate to
		if (
			notification.submission_id &&
			notification.form_name &&
			notification.first_name
		) {
			// This is a real notification with valid submission data
			history.push(
				`/admin/submissions/${notification.submission_id}?from=notifications`
			);
		} else if (notification.metadata?.test_event) {
			// This is a test notification - navigate to submissions list
			history.push('/admin/submissions?from=notifications');
		} else {
			// This is a notification for a deleted/missing submission
			// Show the metadata information or navigate to submissions list
			if (notification.metadata?.form_name) {
				// Navigate to submissions filtered by form if we have that info
				history.push(
					`/admin/submissions?search=${encodeURIComponent(
						notification.metadata.form_name
					)}&from=notifications`
				);
			} else {
				// Just go to submissions list
				history.push('/admin/submissions?from=notifications');
			}
		}

		setDropdownOpen(false); // Close dropdown after navigation
	};

	const handleViewAll = () => {
		// Navigate to notifications page without marking anything as seen
		history.push('/admin/notifications');
		setDropdownOpen(false);
	};

	const handleMarkAllAsSeen = async () => {
		if (notificationCount > 10) {
			const confirmed = window.confirm(
				`Mark all ${notificationCount} notifications as seen? This cannot be undone.`
			);
			if (!confirmed) return;
		}

		await markAllAsSeen();
		setDropdownOpen(false);
	};

	const formatSubmissionTime = (createdAt) => {
		try {
			return formatDistanceToNow(new Date(createdAt), { addSuffix: true });
		} catch (error) {
			return 'Recently';
		}
	};

	return (
		<Dropdown
			trigger={
				<div className='relative'>
					<button
						type='button'
						className='p-2 text-clientBlack hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-200 ease-in-out hover:scale-110'
					title={sseConnected ? 'Real-time updates active' : 'Notifications'}>
						<FiBell className='h-7 w-7' />
						{notificationCount > 0 && (
							<span className='absolute -top-1 -right-1 h-5 w-5 bg-primary text-white text-xs rounded-full flex items-center justify-center font-montserrat-medium shadow-sm'>
								{notificationCount > 99 ? '99+' : notificationCount}
							</span>
						)}
						{/* SSE connection indicator */}
						{sseConnected && (
							<span
								className='absolute bottom-1 left-1 h-2 w-2 bg-primary rounded-full animate-pulse'
								title='Real-time updates active'></span>
						)}
					</button>
				</div>
			}
			align='right'
			width='w-80'
			onOpen={handleDropdownOpen}
			onClose={handleDropdownClose}>
			<div className='px-4 py-3 border-b border-gray-100'>
				<h3 className='text-sm font-montserrat-semibold text-gray-900'>
					Notifications
					{sseConnected && (
						<span
							className='ml-2 text-xs text-green-600'
							title='Real-time updates active'>
							●
						</span>
					)}
				</h3>
				<p className='text-xs text-gray-500 mt-1'>
					{recentLoading
						? 'Loading...'
						: notificationCount === 0
						? 'No new notifications'
						: `${notificationCount} new notification${
								notificationCount !== 1 ? 's' : ''
						  }`}
				</p>
			</div>

			<div className='max-h-80 overflow-y-auto'>
				{recentLoading ? (
					<div className='px-4 py-6 text-center'>
						<div className='animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto'></div>
						<p className='text-sm text-gray-500 mt-2'>
							Loading notifications...
						</p>
					</div>
				) : recentNotifications.length > 0 ? (
					<>
						{recentNotifications.map((notification) => (
							<div
								key={notification.id}
								className='px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0'
								onClick={() => handleNotificationClick(notification)}>
								<div className='flex items-start space-x-3'>
									<div className='flex-shrink-0'>
										{/* Different dot colors based on notification validity */}
										<div
											className={`h-2 w-2 rounded-full mt-2 ${
												notification.submission_id && notification.form_name
													? 'bg-blue-500' // Valid submission
													: notification.metadata?.test_event
													? 'bg-orange-500' // Test event
													: 'bg-gray-400' // Missing/deleted submission
											}`}></div>
									</div>
									<div className='flex-1 min-w-0'>
										<p className='text-sm font-montserrat-medium text-gray-900 truncate'>
											{notification.title}
										</p>
										<p className='text-sm text-gray-600 truncate'>
											{/* Use description if available, otherwise construct from metadata */}
											{notification.description ||
												(notification.metadata?.user_name &&
												notification.metadata?.form_name
													? `${notification.metadata.user_name} submitted ${notification.metadata.form_name}`
													: 'Form submission notification')}
										</p>
										{/* Show additional context for missing submissions */}
										{(!notification.submission_id ||
											!notification.form_name) && (
											<p className='text-xs text-gray-400 mt-1'>
												{notification.metadata?.test_event
													? '(Test notification)'
													: '(Original submission may have been deleted)'}
											</p>
										)}
										<p className='text-xs text-gray-500 mt-1'>
											{formatSubmissionTime(notification.created_at)}
										</p>
									</div>
								</div>
							</div>
						))}
					</>
				) : (
					<div className='px-4 py-6 text-center'>
						<FiBell className='h-8 w-8 text-gray-300 mx-auto mb-2' />
						<p className='text-sm text-gray-500'>No recent notifications</p>
					</div>
				)}
			</div>

			{/* Footer with navigation and actions - always visible */}
			<div className='px-4 py-3 border-t border-gray-100 bg-gray-50'>
				<div className='flex justify-between items-center'>
					<button
						onClick={handleViewAll}
						className='text-sm text-primary hover:text-primary-dark font-montserrat-medium'>
						{notificationCount > 0
							? `View all ${notificationCount} notifications →`
							: 'View all notifications →'}
					</button>

					{notificationCount > 0 && (
						<button
							onClick={handleMarkAllAsSeen}
							className='text-xs text-gray-500 hover:text-gray-700 font-montserrat-medium'>
							Mark all as seen
						</button>
					)}
				</div>
			</div>
		</Dropdown>
	);
};

export default NotificationBell;
